import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  AlertController,
  NavController,
  ToastController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PaginationClientSideOptions } from 'src/app/app/_component/pagination-client-side/pagination-client-side-options';
import { PaginationColumn } from 'src/app/app/_component/pagination-client-side/pagination-column.class';
import { DictionaryService } from 'src/app/dictionary/dictionary.service';
import { HeaderModel } from 'src/app/_component/header/header.model';
import { globalHelper } from 'src/app/_helpers/global';
import { User } from 'src/app/_models/user.model';
import { HeaderService } from 'src/app/_services/header.service';
import { ThisPlatform } from 'src/app/_services/platform.service';
import { RowsLoaderService } from 'src/app/_services/rows-loader.service';
import { UserService } from 'src/app/_services/user.service';
import { Company } from '../../company/company.model';
import { CompanyService } from '../../company/company.service';
import { OrderDetailsViewComponent } from '../order-details-view/order-details-view.component';
import { Order } from '../_model/order.model';
import { OrderService } from '../_services/order.service';

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.page.html',
  styleUrls: [
    './order-details.page.scss',
    './../../../_styles/ion-grid-box.scss',
  ],
})
export class OrderDetailsPage implements OnInit {
  headerInit: HeaderModel = {
    title: 'MARKET.ORDER_NR',
  };

  componentRef: any = OrderDetailsViewComponent;
  columnsDefinition: PaginationColumn[] = [
    { name: 'image', displayName: '', size: 1, sortable: false },
    {
      name: 'productName',
      displayName: 'MARKET.PRODUCT',
      size: 5,
      sortable: true,
    },
    { name: 'amount', displayName: 'MARKET.AMOUNT', size: 2, sortable: true },
    { name: 'price', displayName: 'MARKET.PRICE', size: 2, sortable: true },
    {
      name: 'totalCost',
      displayName: 'MARKET.TOTAL_COST',
      size: 2,
      sortable: true,
    },
  ];

  options: PaginationClientSideOptions = {
    ...new PaginationClientSideOptions(),
    mainStyle: 'gray-md-rows',
  };

  orderId: number;
  rowsLoader: RowsLoaderService;
  companyProvider: Company;
  form: FormGroup;
  disableSaveButton = false;
  statusNames: StatusIdName[] = [];
  currentStatus: StatusIdName;
  rows = [];
  canChangeStatus: boolean;

  isError: boolean = false;
  errorStatus: number;

  rowHeight: number;
  showLoading: boolean;

  constructor(
    private route: ActivatedRoute,
    private navCtrl: NavController,
    public orderServ: OrderService,
    public dictionaryServ: DictionaryService,
    public companyService: CompanyService,
    private translate: TranslateService,
    public p: ThisPlatform,
    public userServ: UserService,
    private toast: ToastController,
    private alertCtrl: AlertController,
    private headerServ: HeaderService
  ) {}

  async ngOnInit() {
    this.showLoading = true;
    await this.statusToIdNamesType();
    this.form = new FormGroup({
      status: new FormControl('', {
        updateOn: 'blur',
      }),
    });
    await this.route.paramMap.subscribe(async (paramMap) => {
      if (!paramMap.has('id')) {
        this.navCtrl.navigateBack('/market');
        return;
      }
      this.orderId = Number(paramMap.get('id'));
      this.headerInit.title =
        this.translate.instant(this.headerInit.title) + this.orderId;
      await this.orderServ.getOrder(this.orderId).subscribe(
        (e: Order) => {
          this.orderServ.order = e;
          this.canChangeStatus = this.userServ.hasRoles([
            'market.order.change.status',
          ]);
          this.currentStatus = this.statusNames.find((e: StatusIdName) => {
            if (this.orderServ.order.status === e.id) {
              return e.name;
            }
          });
          this.form.patchValue({
            status: this.currentStatus,
          });
          this.companyService
            .GetOne(this.orderServ.order.idCompanyProvider)
            .subscribe((result) => {
              this.companyProvider = result as Company;
            });
          this.userServ
            .getById(this.orderServ.order.idUser)
            .subscribe((result) => {
              this.userServ.user = result as User;
            });
        },
        (err) => {
          this.isError = true;
          this.errorStatus = err.status;
          return;
        }
      );
    });
  }

  ionViewWillEnter() {
    this.headerServ.setHeaderInit(this.headerInit);
    this.showLoading = false;
  }

  rowsLoaderServiceReady(loader: RowsLoaderService) {
    this.rowsLoader = loader;
    this.rowsLoaderInit();
  }

  rowsLoaderInit() {
    if (this.rowsLoader) {
      this.rowsLoader.init([...this.orderServ.order.orderItems]);
    }
  }

  async onValueChanged(event) {
    let conf, mess: string;
    if (!(event?.length === 0 || event?.id === 0) && event) {
      mess =
        this.translate.instant('MARKET.CHANGE_STATUS') +
        '"' +
        event.name +
        '"?';
      conf = await globalHelper.Alert(
        this.alertCtrl,
        this.translate,
        mess,
        null,
        null,
        false
      );
    } else if (event?.id === this.currentStatus.id) {
      return;
    } else {
      mess =
        this.translate.instant('MARKET.CANNOT_CHANGE_STATUS') +
        '"' +
        (event?.name ? event.name : '') +
        '".';
      await globalHelper.Alert(
        this.alertCtrl,
        this.translate,
        mess,
        null,
        null,
        false
      );
      this.form.patchValue({
        status: this.currentStatus,
      });
      conf = false;
    }
    if (conf) {
      this.disableSaveButton = true;
      const object = {
        status: event.id,
      } as Order;
      this.orderServ.updateOrder(this.orderServ.order.id, object).subscribe();
      globalHelper.PresentToast(this.toast, this.translate);
      this.currentStatus = { ...this.form.value.status };
      this.disableSaveButton = false;
    } else {
      this.form.patchValue({
        status: this.currentStatus,
      });
    }
  }

  async statusToIdNamesType() {
    if (!this.dictionaryServ.dictionaryMarketOrderStatus) {
      await this.dictionaryServ.refreshDictionary();
    }
    this.dictionaryServ.dictionaryMarketOrderStatus.forEach((e) => {
      this.statusNames.push(new StatusIdName(e.value, e.key));
    });
  }
}

// Klasa pomocnicza
class StatusIdName {
  id: number;
  name: string;

  constructor(id: number, name: string) {
    this.id = id;
    this.name = name;
  }
}
